import type { GbpMappingAccount } from '../types/gbpOnboarding.js';

// GBP agency onboarding — PURE view helpers for the selective mapping table
// (unit-tested). No I/O.

export type GbpAccountStatus = 'connected' | 'needs-profile' | 'not-connected';

/**
 * Per-row status for the mapping table's badge:
 *  - 'connected'     — GBP is already linked (don't double-connect)
 *  - 'needs-profile' — no Zernio profile yet (connect Meta first; not connectable)
 *  - 'not-connected' — eligible to connect now
 */
export function mappingStatus(account: GbpMappingAccount): GbpAccountStatus {
  if (account.alreadyConnected) return 'connected';
  if (!account.hasProfile) return 'needs-profile';
  return 'not-connected';
}

/** A row can be connected only when it has a profile and isn't already connected. */
export function isConnectable(account: GbpMappingAccount): boolean {
  return mappingStatus(account) === 'not-connected';
}

export interface MappingFilter {
  search?: string;
  // Show only rows that can be connected right now (has profile, not connected).
  onlyConnectable?: boolean;
  // Hide rows that already have GBP connected.
  hideConnected?: boolean;
}

/**
 * Filter the roster for the mapping table. Search matches name OR accountType
 * (case-insensitive). Selective-by-design: the operator narrows to the specific
 * accounts they want — nothing is pre-selected. Result order is preserved.
 */
export function filterMappingAccounts(
  accounts: GbpMappingAccount[],
  filter: MappingFilter,
): GbpMappingAccount[] {
  const needle = filter.search?.trim().toLowerCase() ?? '';
  return accounts.filter((a) => {
    if (filter.onlyConnectable && !isConnectable(a)) return false;
    if (filter.hideConnected && a.alreadyConnected) return false;
    if (needle) {
      const hay = `${a.name} ${a.accountType}`.toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  });
}

/** Count how many rows the operator has staged a location selection for. */
export function countStagedSelections(
  selections: Record<string, { locationId: string } | undefined>,
): number {
  return Object.values(selections).filter((s) => Boolean(s?.locationId)).length;
}

// ---------------------------------------------------------------------------
// v2 (assignGoogleBusinessLocation) — assign outcome + token-holder selection.
// ---------------------------------------------------------------------------

// The result of an assign attempt, derived from the HTTP status. A 409 means the
// TARGET profile already has a GBP connection (one per profile) → offer "switch
// location" via updateGmbLocation instead of re-assigning.
export type GbpAssignOutcome = 'assigned' | 'needs_switch' | 'error';

export function assignOutcomeFromStatus(status: number): GbpAssignOutcome {
  if (status === 409) return 'needs_switch';
  if (status >= 200 && status < 300) return 'assigned';
  return 'error';
}

// Pick the grant source (token holder) to assign through: the operator's
// preferred one if still present, else the first available. Null when none exist
// (the operator must connect one account's GBP first — the per-account button).
export function pickTokenHolder<T extends { accountId: string }>(
  holders: T[],
  preferredId?: string | null,
): T | null {
  if (holders.length === 0) return null;
  if (preferredId) {
    const found = holders.find((h) => h.accountId === preferredId);
    if (found) return found;
  }
  return holders[0]!;
}

// Label the GBP grant SOURCE by the connected GOOGLE ACCOUNT identity (the login
// that authorized the agency grant, e.g. "google@kyck.com.au"), NOT the kyck
// location — the grant is reusable across many locations, so a per-account name is
// misleading. Prefer the authorizing account's username (Zernio
// metadata.userProfile.username, cached as connectedByUsername), then the GBP
// SocialAccount's display name / username, then the kyck account name, so it NEVER
// renders blank.
//
// ⚠️ VERIFY (live): what `connectedByUsername` actually holds for a real GBP grant
// is UNCONFIRMED — Zernio's userProfile.username may be the Google email, a bare
// username, or a display name. If it's empty/numeric/unhelpful for the live
// Liverpool Mazda grant, capture the Google email at connect time instead (the
// fallback keeps the UI sensible meanwhile). See the brief's flag.
export function pickGrantSourceIdentity(input: {
  connectedByUsername?: string | null;
  displayName?: string | null;
  username?: string | null;
  accountName?: string | null;
}): string {
  const firstNonEmpty = (...vals: Array<string | null | undefined>): string | null => {
    for (const v of vals) {
      if (typeof v === 'string' && v.trim() !== '') return v.trim();
    }
    return null;
  };
  return (
    firstNonEmpty(
      input.connectedByUsername,
      input.displayName,
      input.username,
      input.accountName,
    ) ?? 'Connected Google account'
  );
}
